// @archigraph r8DljQiU
// @archigraph authenticates|ui.website|auth.cognito|security (uuid: 2kBDvm7H)

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config } from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
});

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface CognitoUserInfo {
  sub: string;
  email: string;
  name: string;
  emailVerified: boolean;
}

function extractTokens(session: CognitoUserSession): CognitoTokens {
  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    expiresAt: session.getAccessToken().getExpiration() * 1000,
  };
}

function mapCognitoError(err: unknown): string {
  const error = err as { code?: string; message?: string };
  switch (error.code) {
    case 'UsernameExistsException':
      return 'An account with this email already exists.';
    case 'InvalidPasswordException':
      return 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number.';
    case 'NotAuthorizedException':
      return 'Invalid email or password.';
    case 'UserNotConfirmedException':
      return 'Please verify your email before logging in.';
    case 'TooManyRequestsException':
      return 'Too many attempts. Please try again later.';
    case 'CodeMismatchException':
      return 'Invalid verification code. Please check and try again.';
    case 'ExpiredCodeException':
      return 'Verification code has expired. Please request a new one.';
    case 'InvalidParameterException':
      return 'Please check your input and try again.';
    case 'LimitExceededException':
      return 'Too many attempts. Please try again later.';
    case 'NetworkError':
      return 'Network error. Please check your connection and try again.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
}

export async function cognitoSignUp(
  email: string,
  password: string,
  name: string
): Promise<{ username: string; userSub: string; codeDeliveryDestination: string }> {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
    ];

    // Generate a unique username — email is passed as an alias attribute
    const username = crypto.randomUUID();

    userPool.signUp(username, password, attributes, [], (err, result) => {
      if (err) {
        reject(new Error(mapCognitoError(err)));
        return;
      }
      resolve({
        username,
        userSub: result?.userSub || '',
        codeDeliveryDestination:
          result?.codeDeliveryDetails?.Destination || email,
      });
    });
  });
}

export async function cognitoConfirmSignUp(
  username: string,
  code: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(new Error(mapCognitoError(err)));
        return;
      }
      resolve();
    });
  });
}

export async function cognitoResendConfirmation(
  username: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(new Error(mapCognitoError(err)));
        return;
      }
      resolve();
    });
  });
}

export async function cognitoSignIn(
  email: string,
  password: string
): Promise<{ tokens: CognitoTokens; user: CognitoUserInfo }> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const tokens = extractTokens(session);
        const idTokenPayload = session.getIdToken().decodePayload();
        const user: CognitoUserInfo = {
          sub: idTokenPayload['sub'] as string,
          email: idTokenPayload['email'] as string,
          name: (idTokenPayload['name'] as string) || '',
          emailVerified: idTokenPayload['email_verified'] === true || idTokenPayload['email_verified'] === 'true',
        };
        resolve({ tokens, user });
      },
      onFailure: (err) => {
        reject(new Error(mapCognitoError(err)));
      },
    });
  });
}

export async function cognitoForgotPassword(
  email: string
): Promise<{ codeDeliveryDestination: string }> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        // Called after password is confirmed reset — not used here
      },
      onFailure: (err) => {
        reject(new Error(mapCognitoError(err)));
      },
      inputVerificationCode: (data) => {
        resolve({
          codeDeliveryDestination:
            data?.CodeDeliveryDetails?.Destination || email,
        });
      },
    });
  });
}

export async function cognitoConfirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(new Error(mapCognitoError(err))),
    });
  });
}

export async function cognitoSignOut(): Promise<void> {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

export async function cognitoRefreshSession(): Promise<{
  tokens: CognitoTokens;
  user: CognitoUserInfo;
} | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        const tokens = extractTokens(session);
        cognitoUser.getUserAttributes((attrErr, attributes) => {
          if (attrErr || !attributes) {
            resolve(null);
            return;
          }

          const attrMap: Record<string, string> = {};
          for (const attr of attributes) {
            attrMap[attr.getName()] = attr.getValue();
          }

          const user: CognitoUserInfo = {
            sub: attrMap['sub'] || '',
            email: attrMap['email'] || '',
            name: attrMap['name'] || '',
            emailVerified: attrMap['email_verified'] === 'true',
          };

          resolve({ tokens, user });
        });
      }
    );
  });
}

export async function cognitoGetUser(): Promise<CognitoUserInfo | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        cognitoUser.getUserAttributes((attrErr, attributes) => {
          if (attrErr || !attributes) {
            resolve(null);
            return;
          }

          const attrMap: Record<string, string> = {};
          for (const attr of attributes) {
            attrMap[attr.getName()] = attr.getValue();
          }

          resolve({
            sub: attrMap['sub'] || '',
            email: attrMap['email'] || '',
            name: attrMap['name'] || '',
            emailVerified: attrMap['email_verified'] === 'true',
          });
        });
      }
    );
  });
}
